import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  X, Check, Package, Globe, ShoppingCart, Copy, ChevronRight, Loader2,
  Upload, CreditCard, Receipt, LayoutDashboard, Camera, Smartphone, Play
} from 'lucide-react';

const BRAND = {
  navy: '#0B1F4A',
  blue: '#1A56DB',
  blueLight: '#60A5FA',
  green: '#22c55e',
  greenLight: '#4ade80',
} as const;

type DemoProduct = {
  name: string;
  price: number;
  cost: number;
  stock: number;
  sku: string;
  isOnline: boolean;
  image: string | null;
};

const MOCK_IMAGES = [
  { id: 'tshirt', url: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop', label: 'Camiseta' },
  { id: 'shoes', url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200&h=200&fit=crop', label: 'Zapatos' },
  { id: 'bag', url: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=200&h=200&fit=crop', label: 'Bolso' },
  { id: 'watch', url: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop', label: 'Reloj' },
  { id: 'headphones', url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200&h=200&fit=crop', label: 'Audífonos' },
  { id: 'coffee', url: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=200&h=200&fit=crop', label: 'Café' },
];

type DemoPedido = {
  id: string;
  productName: string;
  quantity: number;
  subtotal: number;
  iva: number;
  total: number;
  buyerName: string;
  buyerPhone: string;
  status: 'pending' | 'confirmed' | 'completed';
  paymentStatus: 'unpaid' | 'proof_uploaded' | 'verified' | 'partial' | 'paid';
  amountPaid: number;
  paymentProofUrl: string | null;
  createdAt: Date;
};

interface InteractiveDemoProps {
  isOpen: boolean;
  onClose: () => void;
  videoUrl?: string;
}

const STEPS = [
  { id: 1, title: 'Crear producto', shortTitle: 'Producto', icon: Package },
  { id: 2, title: 'Publicar en tienda', shortTitle: 'Tienda', icon: Globe },
  { id: 3, title: 'Pedido del cliente', shortTitle: 'Pedido', icon: ShoppingCart },
  { id: 4, title: 'Ver en tu panel', shortTitle: 'Panel', icon: LayoutDashboard },
];

const formatCOP = (n: number) => 
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const generateSKU = (productName: string): string => {
  const prefix = productName
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3)
    .padEnd(3, 'X');
  const random = Math.floor(Math.random() * 9000) + 1000;
  return `${prefix}-${random}`;
};

const IVA_RATE = 0.19;

export const InteractiveDemo: React.FC<InteractiveDemoProps> = ({ isOpen, onClose, videoUrl }) => {
  const [step, setStep] = useState(1);
  const [product, setProduct] = useState<DemoProduct>({
    name: '',
    price: 0,
    cost: 0,
    stock: 0,
    sku: '',
    isOnline: false,
    image: null,
  });
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [pedido, setPedido] = useState<DemoPedido | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepCompleted, setStepCompleted] = useState<Record<number, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [showBuyerView, setShowBuyerView] = useState(false);
  const [buyerQuantity, setBuyerQuantity] = useState(1);
  const [showPaymentProofUpload, setShowPaymentProofUpload] = useState(false);
  const [paymentProofUploaded, setPaymentProofUploaded] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const demoStoreUrl = `clientelo.com.co/mi-tienda`;

  const resetDemo = useCallback(() => {
    setStep(1);
    setProduct({ name: '', price: 0, cost: 0, stock: 0, sku: '', isOnline: false, image: null });
    setPedido(null);
    setStepCompleted({});
    setCopied(false);
    setShowBuyerView(false);
    setBuyerQuantity(1);
    setShowImagePicker(false);
    setShowPaymentProofUpload(false);
    setPaymentProofUploaded(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(resetDemo, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, resetDemo]);

  const handleProductNameChange = useCallback((name: string) => {
    setProduct(prev => ({
      ...prev,
      name,
      sku: name.trim().length >= 2 ? generateSKU(name) : '',
    }));
  }, []);

  const handleAddProduct = useCallback(() => {
    const name = product.name.trim();
    const price = product.price;
    const stock = product.stock;
    const currentSku = product.sku;
    
    if (!name || price <= 0 || stock <= 0) return;
    
    setIsSubmitting(true);
    
    window.setTimeout(() => {
      if (!currentSku) {
        setProduct(prev => ({ ...prev, sku: generateSKU(prev.name) }));
      }
      setStepCompleted(prev => ({ ...prev, 1: true }));
      setIsSubmitting(false);
    }, 600);
  }, [product.name, product.price, product.stock, product.sku]);

  const handleToggleOnline = useCallback(() => {
    setIsSubmitting(true);
    setTimeout(() => {
      setProduct(prev => ({ ...prev, isOnline: true }));
      setStepCompleted(prev => ({ ...prev, 2: true }));
      setIsSubmitting(false);
    }, 400);
  }, []);

  const handleCopyLink = useCallback(async () => {
    await navigator.clipboard.writeText(`https://${demoStoreUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [demoStoreUrl]);

  const handleBuyerOrder = useCallback(() => {
    const productName = product.name;
    const productPrice = product.price;
    const qty = buyerQuantity;
    
    setIsSubmitting(true);
    setTimeout(() => {
      const subtotal = productPrice * qty;
      const iva = Math.round(subtotal * IVA_RATE);
      const total = subtotal + iva;
      
      setPedido({
        id: `PED-${Math.floor(Math.random() * 9000) + 1000}`,
        productName,
        quantity: qty,
        subtotal,
        iva,
        total,
        buyerName: 'María González',
        buyerPhone: '+57 300 123 4567',
        status: 'pending',
        paymentStatus: 'unpaid',
        amountPaid: 0,
        paymentProofUrl: null,
        createdAt: new Date(),
      });
      setShowBuyerView(false);
      setIsSubmitting(false);
    }, 800);
  }, [product.name, product.price, buyerQuantity]);

  const handleUploadProof = useCallback(() => {
    setIsSubmitting(true);
    setTimeout(() => {
      setPedido(prev => prev ? {
        ...prev,
        paymentStatus: 'proof_uploaded',
        paymentProofUrl: 'comprobante_simulado.jpg',
      } : null);
      
      setPaymentProofUploaded(true);
      setShowPaymentProofUpload(false);
      setStepCompleted(prev => ({ ...prev, 3: true }));
      setIsSubmitting(false);
    }, 1000);
  }, []);

  const handleVerifyPayment = useCallback(() => {
    if (!pedido) return;
    
    const pedidoQuantity = pedido.quantity;
    
    setIsSubmitting(true);
    setTimeout(() => {
      setPedido(prev => prev ? {
        ...prev,
        status: 'completed',
        paymentStatus: 'paid',
        amountPaid: prev.total,
      } : null);
      
      setProduct(prev => ({
        ...prev,
        stock: Math.max(0, prev.stock - pedidoQuantity),
      }));
      
      setStepCompleted(prev => ({ ...prev, 4: true }));
      setIsSubmitting(false);
    }, 600);
  }, [pedido]);

  const canProceedStep1 = product.name.trim() && product.price > 0 && product.stock > 0;

  const orderStats = useMemo(() => {
    if (!pedido) return null;
    const margin = product.price - product.cost;
    const profit = margin * pedido.quantity;
    return { margin, profit };
  }, [pedido, product]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-title"
    >
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100" style={{ backgroundColor: BRAND.navy }}>
          <div className="min-w-0 flex-1">
            <h2 id="demo-title" className="text-base sm:text-lg font-bold text-white truncate">
              Prueba Clientelo en 2 minutos
            </h2>
            <p className="text-xs sm:text-sm text-white/70">Demo interactivo · Sin registro</p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {videoUrl && (
              <button
                onClick={() => setShowVideoModal(true)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Ver video
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Cerrar demo"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-3 sm:px-5 py-3 sm:py-4 bg-gray-50 border-b border-gray-100 overflow-x-auto">
          <div className="flex items-center justify-between min-w-[320px] sm:min-w-0 gap-1 sm:gap-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.id;
              const isDone = stepCompleted[s.id];
              
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => isDone && setStep(s.id)}
                    disabled={!isDone && s.id !== step}
                    className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all shrink-0 ${
                      isActive 
                        ? 'bg-white shadow-sm ring-1 ring-gray-200' 
                        : isDone 
                          ? 'hover:bg-white/50 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
                      isDone 
                        ? 'bg-green-500 text-white'
                        : isActive
                          ? 'text-white'
                          : 'bg-gray-200 text-gray-400'
                    }`}
                    style={isActive && !isDone ? { backgroundColor: BRAND.blue } : undefined}
                    >
                      {isDone ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : <Icon className="w-3 h-3 sm:w-4 sm:h-4" />}
                    </div>
                    <div className="text-left">
                      <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">Paso {s.id}</p>
                      <p className={`text-xs sm:text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>
                        <span className="hidden sm:inline">{s.title}</span>
                        <span className="sm:hidden">{s.shortTitle}</span>
                      </p>
                    </div>
                  </button>
                  {i < STEPS.length - 1 && (
                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300 shrink-0 hidden sm:block" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {/* Step 1: Create Product with Auto SKU */}
          {step === 1 && (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Crea tu primer producto</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  Agrega un producto de ejemplo. El código SKU se genera automáticamente.
                </p>
              </div>

              <div className="space-y-4">
                {/* Image Picker */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Foto del producto</label>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <button
                      type="button"
                      onClick={() => !stepCompleted[1] && setShowImagePicker(!showImagePicker)}
                      disabled={stepCompleted[1]}
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-2 border-dashed flex items-center justify-center transition-all overflow-hidden ${
                        stepCompleted[1] 
                          ? 'border-gray-200 cursor-not-allowed' 
                          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
                      }`}
                    >
                      {product.image ? (
                        <img src={product.image} alt="Producto" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                      )}
                    </button>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                      {product.image ? 'Click para cambiar' : 'Click para elegir'}
                    </p>
                  </div>
                  
                  {showImagePicker && !stepCompleted[1] && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Elige una imagen de ejemplo:</p>
                      <div className="grid grid-cols-6 gap-2">
                        {MOCK_IMAGES.map(img => (
                          <button
                            key={img.id}
                            type="button"
                            onClick={() => {
                              setProduct(p => ({ ...p, image: img.url }));
                              setShowImagePicker(false);
                            }}
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden border-2 transition-all ${
                              product.image === img.url 
                                ? 'border-blue-500 ring-2 ring-blue-200' 
                                : 'border-transparent hover:border-gray-300'
                            }`}
                            title={img.label}
                          >
                            <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Nombre del producto</label>
                  <input
                    type="text"
                    value={product.name}
                    onChange={e => handleProductNameChange(e.target.value)}
                    placeholder="Ej: Camiseta básica negra"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                    disabled={stepCompleted[1]}
                  />
                </div>

                {/* Auto SKU Display */}
                {product.name.trim().length >= 2 && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Receipt className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-blue-600 font-medium">SKU generado automáticamente</p>
                      <p className="text-sm font-mono font-bold text-blue-800 truncate">{product.sku}</p>
                    </div>
                    <span className="text-[10px] sm:text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium whitespace-nowrap">
                      Auto SKU ✓
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Precio venta</label>
                    <input
                      type="number"
                      value={product.price || ''}
                      onChange={e => setProduct(p => ({ ...p, price: Number(e.target.value) || 0 }))}
                      placeholder="45000"
                      className="w-full px-2 sm:px-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      disabled={stepCompleted[1]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Costo</label>
                    <input
                      type="number"
                      value={product.cost || ''}
                      onChange={e => setProduct(p => ({ ...p, cost: Number(e.target.value) || 0 }))}
                      placeholder="25000"
                      className="w-full px-2 sm:px-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      disabled={stepCompleted[1]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Stock</label>
                    <input
                      type="number"
                      value={product.stock || ''}
                      onChange={e => setProduct(p => ({ ...p, stock: Number(e.target.value) || 0 }))}
                      placeholder="10"
                      className="w-full px-2 sm:px-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      disabled={stepCompleted[1]}
                    />
                  </div>
                </div>

                {product.price > 0 && product.cost > 0 && (
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <span className="text-gray-500">Margen:</span>
                    <span className={`font-semibold ${product.price > product.cost ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCOP(product.price - product.cost)} ({Math.round(((product.price - product.cost) / product.price) * 100)}%)
                    </span>
                  </div>
                )}
              </div>

              {stepCompleted[1] && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-green-800 truncate">{product.name}</p>
                    <p className="text-xs sm:text-sm text-green-600">
                      {formatCOP(product.price)} · SKU: {product.sku} · {product.stock} uds
                    </p>
                  </div>
                  <Check className="w-5 h-5 text-green-500 shrink-0" />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Publish to Online Store */}
          {step === 2 && (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Ponlo en tu tienda online</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  Con un clic tu producto está visible en tu Vitrina Virtual. Comparte el link con tus clientes.
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500">{formatCOP(product.price)} · SKU: {product.sku}</p>
                  </div>
                  {!product.isOnline ? (
                    <button
                      onClick={handleToggleOnline}
                      disabled={isSubmitting}
                      className="px-3 sm:px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                      style={{ backgroundColor: BRAND.blue }}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Globe className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">Publicar</span>
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-green-100 text-green-700 text-xs sm:text-sm font-medium rounded-full">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                      En línea
                    </span>
                  )}
                </div>

                {product.isOnline && (
                  <div className="pt-4 border-t border-gray-200 space-y-3">
                    <p className="text-xs sm:text-sm font-medium text-gray-700">Link de tu tienda:</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs sm:text-sm text-gray-600 truncate">
                        https://{demoStoreUrl}
                      </div>
                      <button
                        onClick={handleCopyLink}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                        title="Copiar link"
                      >
                        {copied ? <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {stepCompleted[2] && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs sm:text-sm text-blue-800">
                    <strong>¡Listo!</strong> Tu producto ya está visible. Ahora simula un pedido de un cliente.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Customer Order with Payment Proof */}
          {step === 3 && (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Un cliente hace un pedido</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  El cliente ve tu producto, hace el pedido y sube el comprobante de pago.
                </p>
              </div>

              {!pedido && !showBuyerView && (
                <button
                  onClick={() => setShowBuyerView(true)}
                  className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Smartphone className="w-5 h-5" />
                  Simular vista del cliente
                </button>
              )}

              {/* Buyer View Simulation */}
              {showBuyerView && !pedido && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4 text-xs sm:text-sm text-gray-500">
                    <Smartphone className="w-4 h-4" />
                    Vista del cliente · {demoStoreUrl}
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Package className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{product.name}</p>
                        <p className="text-base sm:text-lg font-bold" style={{ color: BRAND.blue }}>{formatCOP(product.price)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                      <label className="text-xs sm:text-sm text-gray-600">Cantidad:</label>
                      <select
                        value={buyerQuantity}
                        onChange={e => setBuyerQuantity(Number(e.target.value))}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      >
                        {[1, 2, 3].filter(n => n <= product.stock).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <span className="text-xs sm:text-sm text-gray-400">({product.stock} disponibles)</span>
                    </div>

                    <div className="border-t border-gray-100 pt-3 mb-4 text-xs sm:text-sm">
                      <div className="flex justify-between text-gray-600 mb-1">
                        <span>Subtotal</span>
                        <span>{formatCOP(product.price * buyerQuantity)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 mb-1">
                        <span>IVA (19%)</span>
                        <span>{formatCOP(Math.round(product.price * buyerQuantity * IVA_RATE))}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-gray-900 text-sm sm:text-base pt-1 border-t border-gray-100">
                        <span>Total</span>
                        <span>{formatCOP(Math.round(product.price * buyerQuantity * (1 + IVA_RATE)))}</span>
                      </div>
                    </div>

                    <button
                      onClick={handleBuyerOrder}
                      disabled={isSubmitting}
                      className="w-full py-2.5 sm:py-3 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: BRAND.green }}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                          Hacer pedido
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Order Created - Payment Proof Upload */}
              {pedido && pedido.paymentStatus === 'unpaid' && !showPaymentProofUpload && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                        <span className="text-xl">🔔</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-amber-900">¡Pedido #{pedido.id} creado!</p>
                        <p className="text-xs sm:text-sm text-amber-700 mt-1">
                          {pedido.buyerName} quiere {pedido.quantity}× {pedido.productName}
                        </p>
                        <p className="text-base sm:text-lg font-bold text-amber-900 mt-1">{formatCOP(pedido.total)}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowPaymentProofUpload(true)}
                    className="w-full p-4 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="w-5 h-5" />
                    El cliente sube comprobante de pago
                  </button>
                </div>
              )}

              {/* Payment Proof Upload Simulation */}
              {showPaymentProofUpload && !paymentProofUploaded && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4 text-xs sm:text-sm text-blue-600">
                    <Smartphone className="w-4 h-4" />
                    Vista del cliente · Subir comprobante
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                      <CreditCard className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="font-semibold text-gray-900 mb-1">Pedido #{pedido?.id}</p>
                    <p className="text-lg font-bold mb-4" style={{ color: BRAND.blue }}>
                      {pedido && formatCOP(pedido.total)}
                    </p>
                    
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 mb-4">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs sm:text-sm text-gray-500">
                        Toca para subir foto del comprobante
                      </p>
                    </div>

                    <button
                      onClick={handleUploadProof}
                      disabled={isSubmitting}
                      className="w-full py-2.5 sm:py-3 text-white text-sm font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      style={{ backgroundColor: BRAND.blue }}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          Simular subida de comprobante
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Payment Proof Uploaded */}
              {pedido && pedido.paymentStatus === 'proof_uploaded' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">¡Comprobante subido!</p>
                      <p className="text-xs sm:text-sm text-green-600 mt-1">
                        El cliente subió el comprobante de pago. Ahora revísalo en tu panel.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: View in Dashboard Panel */}
          {step === 4 && (
            <div className="space-y-4 sm:space-y-5">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Tu panel de control</h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  Ve el pedido, verifica el pago y completa la venta. El stock se actualiza automáticamente.
                </p>
              </div>

              {pedido && (
                <div className="space-y-4">
                  {/* Order Card */}
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: BRAND.navy }}>
                      <p className="font-semibold text-white flex items-center gap-2 text-sm">
                        <Receipt className="w-4 h-4" />
                        Pedido {pedido.id}
                      </p>
                      <span className={`px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                        pedido.paymentStatus === 'paid' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {pedido.paymentStatus === 'paid' ? 'Pagado' : 'Por verificar'}
                      </span>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      {/* Customer & Product Info */}
                      <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                        {product.image ? (
                          <img src={product.image} alt={product.name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover" />
                        ) : (
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Package className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{pedido.quantity}× {pedido.productName}</p>
                          <p className="text-xs sm:text-sm text-gray-500">{pedido.buyerName}</p>
                          <p className="text-xs text-gray-400">{pedido.buyerPhone}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-base sm:text-lg font-bold" style={{ color: BRAND.navy }}>{formatCOP(pedido.total)}</p>
                          <p className="text-[10px] sm:text-xs text-gray-400">IVA inc.</p>
                        </div>
                      </div>

                      {/* Payment Proof Preview */}
                      {pedido.paymentProofUrl && pedido.paymentStatus !== 'paid' && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                              <CreditCard className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs sm:text-sm font-medium text-blue-800">Comprobante adjunto</p>
                              <p className="text-xs text-blue-600 truncate">{pedido.paymentProofUrl}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Verify Payment Button */}
                      {pedido.paymentStatus !== 'paid' && (
                        <button
                          onClick={handleVerifyPayment}
                          disabled={isSubmitting}
                          className="w-full py-3 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm"
                          style={{ backgroundColor: BRAND.green }}
                        >
                          {isSubmitting ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              Verificar pago y completar venta
                            </>
                          )}
                        </button>
                      )}

                      {/* Completed Sale Summary */}
                      {pedido.paymentStatus === 'paid' && (
                        <div className="space-y-3">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <Check className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-green-800 text-sm">¡Venta completada!</p>
                              <p className="text-xs text-green-600">Pago verificado · Stock actualizado</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2 sm:gap-3">
                            <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
                              <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Stock</p>
                              <p className="text-lg sm:text-xl font-bold text-gray-900">{product.stock}</p>
                              <p className="text-[10px] sm:text-xs text-gray-400">uds</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                              <p className="text-[10px] sm:text-xs text-green-600 uppercase tracking-wide">Venta</p>
                              <p className="text-lg sm:text-xl font-bold text-green-700">{formatCOP(pedido.total)}</p>
                              <p className="text-[10px] sm:text-xs text-green-500">cobrado</p>
                            </div>
                            {orderStats && (
                              <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                                <p className="text-[10px] sm:text-xs text-blue-600 uppercase tracking-wide">Ganancia</p>
                                <p className="text-lg sm:text-xl font-bold text-blue-700">{formatCOP(orderStats.profit)}</p>
                                <p className="text-[10px] sm:text-xs text-blue-500">neto</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2 sm:gap-4">
          <button
            onClick={onClose}
            className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Salir
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            {step > 1 && !stepCompleted[4] && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                ← Anterior
              </button>
            )}

            {step === 1 && !stepCompleted[1] && (
              <button
                onClick={handleAddProduct}
                disabled={!canProceedStep1 || isSubmitting}
                className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: BRAND.blue }}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Guardar
              </button>
            )}

            {step === 1 && stepCompleted[1] && (
              <button
                onClick={() => setStep(2)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: BRAND.blue }}
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 2 && stepCompleted[2] && (
              <button
                onClick={() => setStep(3)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: BRAND.blue }}
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 3 && stepCompleted[3] && (
              <button
                onClick={() => setStep(4)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
                style={{ backgroundColor: BRAND.blue }}
              >
                Ver en panel
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 4 && stepCompleted[4] && (
              <a
                href="/registro"
                className="px-4 sm:px-6 py-2 sm:py-2.5 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-lg"
                style={{ backgroundColor: BRAND.green }}
              >
                Empieza gratis
                <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && videoUrl && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative bg-black rounded-2xl overflow-hidden max-w-4xl w-full aspect-video"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              aria-label="Cerrar video"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src={videoUrl}
              title="Video demo de Clientelo"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveDemo;
